import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Instagram, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import BrandTicker from '../components/BrandTicker';
import { API } from '../context/AuthContext';

export function parseInstagramUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const m = u.pathname.match(/\/(p|reel|reels|tv)\/([A-Za-z0-9_-]+)/);
    if (!m) return null;
    return { kind: m[1] === 'reels' ? 'reel' : m[1], code: m[2] };
  } catch {
    return null;
  }
}

function parseYouTubeUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) {
      const id = u.pathname.slice(1).split('/')[0];
      return id ? { id } : null;
    }
    if (u.hostname.includes('youtube.com')) {
      const id = u.searchParams.get('v');
      if (id) return { id };
      const m = u.pathname.match(/\/(shorts|embed)\/([A-Za-z0-9_-]+)/);
      if (m) return { id: m[2] };
    }
    return null;
  } catch {
    return null;
  }
}

export function cleanInstagramUrl(url) {
  const p = parseInstagramUrl(url);
  if (!p) return url;
  return `https://www.instagram.com/${p.kind}/${p.code}/`;
}

export function embedSrcFor(url) {
  const ig = parseInstagramUrl(url);
  if (ig) {
    return { src: `https://www.instagram.com/${ig.kind}/${ig.code}/embed/`, kind: 'instagram' };
  }
  const yt = parseYouTubeUrl(url);
  if (yt) {
    return { src: `https://www.youtube.com/embed/${yt.id}`, kind: 'youtube' };
  }
  return null;
}

const PER_PAGE_OPTIONS = [3, 6, 9, 12];
const CARD_HEIGHT = 720; // px — uniform IG embed height for clean alignment

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(6);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/campaigns`);
        if (mounted) setCampaigns(data);
      } catch {
        if (mounted) setCampaigns([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const totalPages = Math.max(1, Math.ceil(campaigns.length / perPage));
  const pageItems = useMemo(
    () => campaigns.slice(page * perPage, page * perPage + perPage),
    [campaigns, page, perPage]
  );
  useEffect(() => { setPage(0); }, [perPage]);

  return (
    <div data-testid="page-campaigns">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-16">
        <p className="text-xs tracking-[0.3em] uppercase text-[#3a5a4e] mb-6">Campaigns</p>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tightest text-[#1a3a2e] max-w-5xl">
          Celebrity &amp; Influencer Collabs, <em className="font-light">live from the feed.</em>
        </h1>
      </section>

      <BrandTicker tone="dark" />

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        {!loading && campaigns.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
            <p className="text-xs tracking-[0.25em] uppercase text-[#3a5a4e]">
              Showing {page * perPage + 1}–{Math.min((page + 1) * perPage, campaigns.length)} of {campaigns.length}
            </p>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-[#3a5a4e] tracking-[0.2em] uppercase mr-1">Per page</span>
              {PER_PAGE_OPTIONS.map((n) => (
                <button key={n} onClick={() => setPerPage(n)} data-testid={`perpage-${n}`}
                  className={`px-3 py-1.5 border ${perPage === n ? 'bg-[#1a3a2e] text-[#f5f1e8] border-[#1a3a2e]' : 'border-[#1a3a2e]/30 text-[#1a3a2e]'}`}>
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-[#3a5a4e] text-center py-20" data-testid="campaigns-loading">Loading campaigns…</p>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-24 border border-[#1a3a2e]/15" data-testid="campaigns-empty">
            <p className="font-serif text-3xl text-[#1a3a2e]">Campaigns coming soon.</p>
            <p className="mt-3 text-[#3a5a4e]">
              Add Instagram links from the admin panel — they'll embed automatically here.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {pageItems.map((c, i) => (
                <CampaignCard key={c.id} campaign={c} index={i} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-14 flex items-center justify-center gap-2">
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} data-testid="page-prev"
                  className="p-3 border border-[#1a3a2e]/30 text-[#1a3a2e] hover:bg-[#1a3a2e] hover:text-[#f5f1e8] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#1a3a2e] transition-colors"
                  aria-label="Previous page">
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button key={i} onClick={() => setPage(i)} data-testid={`page-${i}`}
                    className={`min-w-10 h-10 px-3 text-sm border ${i === page ? 'bg-[#1a3a2e] text-[#f5f1e8] border-[#1a3a2e]' : 'border-[#1a3a2e]/30 text-[#1a3a2e]'}`}>
                    {i + 1}
                  </button>
                ))}
                <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} data-testid="page-next"
                  className="p-3 border border-[#1a3a2e]/30 text-[#1a3a2e] hover:bg-[#1a3a2e] hover:text-[#f5f1e8] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#1a3a2e] transition-colors"
                  aria-label="Next page">
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function CampaignCard({ campaign, index }) {
  const cleanUrl = parseInstagramUrl(campaign.instagram_url)
    ? cleanInstagramUrl(campaign.instagram_url)
    : campaign.instagram_url;
  const embed = embedSrcFor(campaign.instagram_url);

  return (
    <motion.div
      data-testid={`campaign-card-${index}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: (index % 6) * 0.05 }}
      className="bg-[#f5f1e8] border border-[#1a3a2e]/15 flex flex-col"
    >
      {/* Embed area — fixed height for grid alignment */}
      <div
        className="relative w-full bg-[#f5f1e8] flex items-center justify-center overflow-hidden"
        style={{ height: `${CARD_HEIGHT}px` }}
      >
        {embed ? (
          <iframe
            src={embed.src}
            title={`Post by ${campaign.brand_name || 'campaign'}`}
            allow="encrypted-media; clipboard-write; picture-in-picture; web-share; accelerometer; autoplay; gyroscope"
            allowFullScreen
            scrolling="no"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="border-0"
            style={{
              width: '100%',
              height: '100%',
              maxWidth: embed.kind === 'youtube' ? '100%' : '540px',
            }}
          />
        ) : (
          <FallbackTile campaign={campaign} />
        )}
      </div>

      {/* Footer strip with brand + open link */}
      <div className="px-5 py-3 flex items-center justify-between border-t border-[#1a3a2e]/10 bg-[#f5f1e8]">
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#3a5a4e] truncate">
            {campaign.brand_name || 'Campaign'}
          </p>
          {campaign.celebrity_name && (
            <p className="font-serif text-base text-[#1a3a2e] truncate">{campaign.celebrity_name}</p>
          )}
        </div>
        <a
          href={cleanUrl}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 inline-flex items-center gap-1.5 text-[11px] text-[#1a3a2e] underline-anim"
        >
          <Instagram size={13} /> Open <ExternalLink size={11} />
        </a>
      </div>
    </motion.div>
  );
}

function FallbackTile({ campaign }) {
  const url = parseInstagramUrl(campaign.instagram_url)
    ? cleanInstagramUrl(campaign.instagram_url)
    : campaign.instagram_url;
  return (
    <div className="flex flex-col items-center justify-center text-center px-6 bg-gradient-to-br from-[#1a3a2e]/5 to-[#e3d9c6] h-full w-full">
      <Instagram size={36} className="text-[#1a3a2e]/60" />
      <p className="mt-4 font-serif text-2xl text-[#1a3a2e]">{campaign.brand_name}</p>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="mt-4 text-sm text-[#1a3a2e] underline-anim"
      >
        View →
      </a>
    </div>
  );
}
